import * as React from 'react'

import * as Sentry from '@sentry/browser'
import {
  type IParseFile,
  type ImporterSessionUploadOptions,
  ImporterSession as nuvoSession,
} from '@ingestro/importer-react'

import app from 'modules/core/store/models/app'
import i18n from 'modules/core/lib/i18n'

import { type EditionData } from './types'

const INVALID_SHEET_PREFIXES = ['Lang', 'v_']
const DOM_POLL_INTERVAL_MS = 100 // Poll every 100ms for DOM to be ready
const MAX_DOM_WAIT_MS = 10000 // Maximum 10 seconds to wait for DOM
const UPLOAD_RETRY_DELAY_MS = 500 // Delay between upload retries
const MAX_UPLOAD_RETRIES = 10 // Maximum upload retry attempts

const transposeData = (data: unknown[][]): unknown[][] => {
  if (!data || data.length === 0) return data

  const maxLength = Math.max(...data.map((row) => row?.length || 0))
  const transposed: unknown[][] = []

  for (let i = 0; i < maxLength; i++) {
    transposed[i] = []
    for (let j = 0; j < data.length; j++) {
      const row = data[j]
      const transposedRow = transposed[i]
      if (transposedRow) {
        transposedRow[j] = row && row[i] !== undefined ? row[i] : ''
      }
    }
  }

  return transposed
}

interface UseNuvoSessionProps {
  importType: string
  parsedFile: IParseFile[] | undefined
  initialData?: EditionData
  loading: boolean
  nuvoImporterRef: React.RefObject<HTMLDivElement>
  headerSelection: boolean
  transpose?: boolean
  isEdition?: boolean
}

export const useNuvoSession = ({
  importType,
  parsedFile,
  initialData,
  loading,
  nuvoImporterRef,
  headerSelection,
  transpose = false,
  isEdition,
}: UseNuvoSessionProps) => {
  const hasVerifiedRef = React.useRef<boolean>(false)
  const hasUploadedRef = React.useRef<boolean>(false)

  /**
   * FIRST useEffect: Handle file upload (NON-EDITION mode only)
   * This effect handles the initial file upload when a user uploads a file.
   * It ONLY runs in non-edition mode (when isEdition === false).
   */
  React.useEffect(() => {
    // CONDITION 1: If this is edition mode, NEVER execute this effect
    // Edition mode uses initialData (handled by second useEffect), not parsedFile
    // This prevents race conditions
    if (isEdition === true) {
      return
    }

    // CONDITION 2: Wait if still loading data
    if (loading) {
      return
    }

    const getSessionData = (): ImporterSessionUploadOptions => {
      if (!parsedFile) return { step: 'mapping', identifier: importType, data: [] }

      const validSheets =
        parsedFile?.[0]?.sheets?.filter((sheet) => {
          return !INVALID_SHEET_PREFIXES.some((prefix) => sheet.sheetName.startsWith(prefix))
        }) || []

      const numberOfSheets = validSheets.length
      const getStep = () => {
        if (numberOfSheets && numberOfSheets > 1) {
          return 'sheetSelection'
        }

        if (!headerSelection) {
          return 'mapping'
        }

        return 'header'
      }
      const getData = () => {
        if (numberOfSheets && numberOfSheets > 1) {
          if (transpose) {
            return parsedFile?.map((file) => ({
              ...file,
              sheets: file.sheets?.map((sheet) => ({
                ...sheet,
                data: transposeData(sheet.data || []),
              })),
            }))
          }
          return parsedFile
        }

        const sheetData = parsedFile?.[0]?.sheets[0]?.data || []
        return transpose ? transposeData(sheetData) : sheetData
      }

      return {
        step: getStep(),
        data: getData(),
        identifier: importType,
      }
    }

    // CONDITION 3: If parsedFile exists, upload and start Nuvo session
    // This determines the initial step based on file structure (sheetSelection, header, or mapping)
    if (parsedFile && parsedFile.length > 0) {
      nuvoSession.upload(getSessionData())
      nuvoSession.start({ identifier: importType })
    }
  }, [parsedFile, importType, loading, headerSelection, transpose, isEdition])

  /**
   * SECOND useEffect: Handle edition mode verification and upload
   * This effect handles the "edition mode" where we load existing data (initialData)
   * and need to ensure Nuvo is fully ready before uploading with step: 'review'.
   *
   * NOTE: nuvoSession.verify() already implements internal polling:
   * - 10 attempts with 500ms intervals (5 seconds total)
   * - Only returns verified: false after all attempts fail
   * So we don't need to implement our own polling loop.
   */

  React.useEffect(() => {
    // CONDITION 1: Check if this is edition mode - if not, skip everything
    if (!isEdition) {
      return
    }

    // CONDITION 2: Check if we have initialData - if not, we don't need to verify
    if (!initialData || initialData.length === 0) {
      return
    }

    // CONDITION 3: If loading, wait for data to finish loading
    if (loading) {
      return
    }

    // CONDITION 4: If we've already verified and uploaded, skip this effect
    if (hasVerifiedRef.current) {
      return
    }

    let cancelled = false

    /**
     * Wait for DOM to be ready, then verify and upload
     */
    const verifyAndUpload = async () => {
      // First, wait for DOM to be mounted (Nuvo component must be in DOM)
      // Add timeout to prevent infinite loop if ref is never assigned
      const startTime = Date.now()
      while (!nuvoImporterRef.current && !cancelled) {
        if (Date.now() - startTime > MAX_DOM_WAIT_MS) {
          // Timeout reached - DOM element never appeared
          const errorMessage = i18n.t(
            'universal_importer.nuvo_importer.verification_error.dom_timeout'
          )
          app.setFlash({
            type: 'wrong',
            message: errorMessage,
            time: 5000,
          })
          return
        }
        await new Promise((resolve) => setTimeout(resolve, DOM_POLL_INTERVAL_MS))
      }

      if (cancelled) {
        return
      }

      try {
        // verify() internally polls 10 times with 500ms intervals
        const result = await nuvoSession.verify({ identifier: importType })

        if (cancelled) {
          return
        }

        if (result.verified) {
          // Now upload the initialData with step: 'review'
          // Retry loop: upload() can fail transiently (e.g. error 1002 when the
          // Nuvo SDK hasn't finished validating its license key). Rather than
          // distinguishing transient from permanent errors, we retry all failures
          // — a truly permanent error will exhaust the retries in ~5s (10 × 500ms)
          // and surface to the user, while a transient one resolves transparently.
          if (!hasUploadedRef.current) {
            let lastError: unknown
            for (let attempt = 0; attempt < MAX_UPLOAD_RETRIES; attempt++) {
              if (cancelled) return
              try {
                await nuvoSession.upload({
                  step: 'review',
                  data: initialData,
                  headerIndex: undefined,
                  identifier: importType,
                })
                if (cancelled) return
                hasUploadedRef.current = true
                hasVerifiedRef.current = true
                nuvoSession.start({ identifier: importType })
                return
              } catch (uploadError) {
                lastError = uploadError
                if (attempt === MAX_UPLOAD_RETRIES - 1) {
                  throw uploadError
                }
                // Wait before retrying — no need to re-verify since the initial
                // verify() already confirmed SDK initialization. We're only waiting
                // for the async license validation to complete.
                await new Promise((resolve) => setTimeout(resolve, UPLOAD_RETRY_DELAY_MS))
                if (cancelled) return
              }
            }
            // Should not reach here, but throw last error if it does
            throw lastError
          }
        } else {
          // Nuvo couldn't verify after 5 seconds of internal polling (10 attempts)
          // This indicates a real initialization problem
          const errorMessage = i18n.t(
            'universal_importer.nuvo_importer.verification_error.verification_failed'
          )

          app.setFlash({
            type: 'wrong',
            message: errorMessage,
            time: 5000,
          })
        }
      } catch (error) {
        // Exception thrown - This indicates a critical error (network, Nuvo API failure, etc.)
        const baseMessage = i18n.t('universal_importer.nuvo_importer.verification_error.exception')
        Sentry.captureException(error, {
          tags: { feature: 'universal_importer', area: 'nuvo_importer' },
          extra: { importType, isEdition: true },
        })

        app.setFlash({
          type: 'wrong',
          message: baseMessage,
          time: 5000,
        })
      }
    }

    verifyAndUpload()

    return () => {
      cancelled = true
    }
  }, [initialData, importType, loading, isEdition, nuvoImporterRef])
}
